import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CommonLayout from '../../../layouts/CommonLayout';
import { allAPiServicesCall } from '../../../services/apiServices';
import { getAuthConfig, getFormDataConfig } from '../../../services/apiUtils';
import imageCompression from 'browser-image-compression';
import JoditEditor from 'jodit-react';
import { ImageCropper, TARGET_ASPECT_RATIO, RECOMMENDED_WIDTH, RECOMMENDED_HEIGHT } from '../../../components/ImageCropper';
import { extractObjectId } from '../../../utils/utils';
import { toast } from 'react-toastify';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';

const AddNews = () => {
    const [title, setTitle] = useState('');
    const [coverImgCreds, setCoverImgCreds] = useState('');
    const [coverImgRightsStatus, setCoverImgRightsStatus] = useState('');
    const [coverImgRightsHolder, setCoverImgRightsHolder] = useState('');
    const [coverImgRightsEvidenceRef, setCoverImgRightsEvidenceRef] = useState('');
    const [description, setDescription] = useState('');
    const [coverImagePath, setCoverImagePath] = useState('');
    const [formLoading, setFormLoading] = useState(false); // Loading for form submission
    const [imageLoading, setImageLoading] = useState(false); // Loading for image upload
    const [author, setAuthor] = useState('');
    const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]); // Default to today's date
    const [readTime, setReadTime] = useState(''); // State for readTime
    const [instagram, setInstagram] = useState(''); // State for Instagram handle
    const [authorPic, setAuthorPic] = useState(''); // State for Author Picture URL
    const [authorDesignation, setAuthorDesignation] = useState(''); // State for Author Designation
    const [authImagePath, setAuthImagrPath] = useState('')
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const editor = useRef(null);
    const [genres, setGenres] = useState([]);
    const [selectedGenres, setSelectedGenres] = useState([]); // State for multiple genres
    const [newGenreInput, setNewGenreInput] = useState(''); // State for new genre input
    const [addingGenre, setAddingGenre] = useState(false); // Loading state for adding genre
    const [genreSearchQuery, setGenreSearchQuery] = useState(''); // State for genre search/filter
    const [showGenreList, setShowGenreList] = useState(false); // State to toggle genre list visibility
    const [taggedArtists, setTaggedArtists] = useState([]); // State for tagged artists
    const [artistSearch, setArtistSearch] = useState(''); // State for artist search
    const [artistResults, setArtistResults] = useState([]); // State for artist search results
    const [showArtistDropdown, setShowArtistDropdown] = useState(false);
    const [isHeroBanner, setIsHeroBanner] = useState(false); // State for hero banner toggle
    const [imageAlt, setImageAlt] = useState(''); // State for cover image alt text
    const [imageCaption, setImageCaption] = useState(''); // State for cover image caption
    const [primaryTag, setPrimaryTag] = useState('Music News'); // State for primary tag badge
    const [articleStatus, setArticleStatus] = useState('published'); // 'draft' | 'published' | 'scheduled'
    const [scheduledPublishDate, setScheduledPublishDate] = useState(''); // ISO datetime for scheduled publish

    const DRAFT_KEY = 'addNewsDraft';
    const [draftStatus, setDraftStatus] = useState('');

    // Video state
    const [videoUrl, setVideoUrl] = useState(''); // State for video URL
    const [videoLoading, setVideoLoading] = useState(false); // Loading state for video upload

    // Tags state
    const [tags, setTags] = useState([]); // All available tags
    const [selectedTags, setSelectedTags] = useState([]); // Selected tag names
    const [newTagInput, setNewTagInput] = useState(''); // New tag name input
    const [newTagType, setNewTagType] = useState('topic'); // New tag type (default to topic)
    const [addingTag, setAddingTag] = useState(false); // Loading state for adding tag
    const [tagSearchQuery, setTagSearchQuery] = useState(''); // State for tag search/filter
    const [showTagList, setShowTagList] = useState(false); // State to toggle tag list visibility

    // Image cropper state
    const [showCropper, setShowCropper] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [aspectWarning, setAspectWarning] = useState(null);
    const coverImageInputRef = useRef(null);

    // Clear all confirmation modal state
    const [showClearModal, setShowClearModal] = useState(false);

    // AI generation state
    const [creationMode, setCreationMode] = useState('manual'); // 'manual' | 'ai'
    const [articleType, setArticleType] = useState('standard_news');
    const [aiTopic, setAiTopic] = useState('');
    const [customPrompt, setCustomPrompt] = useState('');
    const [aiGenerating, setAiGenerating] = useState(false);
    const [topicSuggestions, setTopicSuggestions] = useState([]);
    const [suggestingTopics, setSuggestingTopics] = useState(false);
    const [aiGenerated, setAiGenerated] = useState(false);
    const [showCustomPrompt, setShowCustomPrompt] = useState(false);

    // Author persona state
    const [authors, setAuthors] = useState([]);
    const [selectedAuthorId, setSelectedAuthorId] = useState('');
    const [authorsLoading, setAuthorsLoading] = useState(false);

    // Research data state (for 2-step workflow)
    const [researchData, setResearchData] = useState(null);
    const [selectedResearchTopic, setSelectedResearchTopic] = useState(null);

    const ARTICLE_TYPES = [
        { value: 'standard_news', label: 'Standard News' },
        { value: 'song_review', label: 'Song Review' },
        { value: 'album_review', label: 'Album Review' },
        { value: 'artist_spotlight', label: 'Artist Spotlight' },
        { value: 'opinion_editorial', label: 'Opinion / Editorial' },
        { value: 'culture_feature', label: 'Culture Feature' },
        { value: 'live_coverage', label: 'Live Coverage' },
    ];

    const handleMultipleGenreChange = (genreName) => {
        setSelectedGenres(prev => {
            if (prev.includes(genreName)) {
                return prev.filter(g => g !== genreName);
            } else {
                return [...prev, genreName];
            }
        });
    };

    const fetchGenres = async () => {
        try {
            const response = await allAPiServicesCall.getGenres({}, getAuthConfig())
            // Sort genres alphabetically by genreName
            const sortedGenres = (response.data || []).sort((a, b) =>
                a.genreName.toLowerCase().localeCompare(b.genreName.toLowerCase())
            );
            setGenres(sortedGenres);
        } catch (error) {
            console.error('Error fetching genres:', error);
        }
    };

    const fetchAuthors = async () => {
        setAuthorsLoading(true);
        try {
            const response = await allAPiServicesCall.getAuthors(getAuthConfig());
            if (response?.data) {
                setAuthors(response.data);
            }
        } catch (error) {
            console.error('Error fetching authors:', error);
        } finally {
            setAuthorsLoading(false);
        }
    };

    useEffect(() => {
        fetchGenres();
        fetchTags();
        fetchAuthors();
    }, []);

    const handleAuthorSelect = (authorId) => {
        setSelectedAuthorId(authorId);
        if (!authorId) return;
        const selected = authors.find(a => extractObjectId(a._id) === authorId || a._id === authorId);
        if (selected) {
            setAuthor(selected.name);
            setAuthorDesignation(selected.designation || 'Music Journalist');
            if (selected.image) setAuthImagrPath(selected.image);
        }
    };

    useEffect(() => {
        const storedDraft = localStorage.getItem(DRAFT_KEY);
        if (!storedDraft) return;
        try {
            const draft = JSON.parse(storedDraft);
            if (draft) {
                setTitle(draft.title || '');
                setCoverImgCreds(draft.coverImgCreds || '');
                setCoverImgRightsStatus(draft.coverImgRightsStatus || '');
                setCoverImgRightsHolder(draft.coverImgRightsHolder || '');
                setCoverImgRightsEvidenceRef(draft.coverImgRightsEvidenceRef || '');
                setDescription(draft.description || '');
                setCoverImagePath(draft.coverImagePath || '');
                setAuthor(draft.author || '');
                setDate(draft.date || new Date().toISOString().split('T')[0]);
                setReadTime(draft.readTime || '');
                setInstagram(draft.instagram || '');
                setAuthImagrPath(draft.authImagePath || '');
                setAuthorDesignation(draft.authorDesignation || '');
                setSelectedGenres(draft.selectedGenres || []);
                setSelectedTags(draft.selectedTags || []);
                setTaggedArtists(draft.taggedArtists || []);
                setIsHeroBanner(Boolean(draft.isHeroBanner));
                setImageAlt(draft.imageAlt || '');
                setImageCaption(draft.imageCaption || '');
                setVideoUrl(draft.videoUrl || '');
                setPrimaryTag(draft.primaryTag || 'Music News');
                setArticleStatus(draft.articleStatus || 'published');
                setScheduledPublishDate(draft.scheduledPublishDate || '');
                setCreationMode('manual');
                if (draft.articleType) setArticleType(draft.articleType);
                if (draft.aiTopic) setAiTopic(draft.aiTopic);
            }
            toast.info('Previous Draft restored.', { toastId: 'draft-restored' });
        } catch (error) {
            console.error('Failed to load draft:', error);
        }
    }, []);

    const buildDraft = () => ({
        title,
        coverImgCreds,
        coverImgRightsStatus,
        coverImgRightsHolder,
        coverImgRightsEvidenceRef,
        description,
        coverImagePath,
        author,
        date,
        readTime,
        instagram,
        authImagePath,
        authorDesignation,
        selectedGenres,
        selectedTags,
        taggedArtists: taggedArtists.map((artist) => ({
            _id: extractObjectId(artist._id),
            artist_name: artist.artist_name,
        })),
        isHeroBanner,
        imageAlt,
        imageCaption,
        videoUrl,
        primaryTag,
        articleStatus,
        scheduledPublishDate,
        creationMode,
        articleType,
        aiTopic,
    });

    const handleSaveDraft = (silent = false) => {
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(buildDraft()));
            if (!silent) {
                setDraftStatus('Draft saved');
                setTimeout(() => setDraftStatus(''), 2000);
            }
        } catch (error) {
            console.error('Failed to save draft:', error);
            if (!silent) {
                setDraftStatus('Failed to save draft');
                setTimeout(() => setDraftStatus(''), 2000);
            }
        }
    };

    const handleClearDraft = () => {
        localStorage.removeItem(DRAFT_KEY);
        setDraftStatus('Draft cleared');
        setTimeout(() => setDraftStatus(''), 2000);
    };

    const handleClearAll = () => {
        // Reset all form fields to initial state
        setTitle('');
        setCoverImgCreds('');
        setCoverImgRightsStatus('');
        setCoverImgRightsHolder('');
        setCoverImgRightsEvidenceRef('');
        setDescription('');
        setCoverImagePath('');
        setAuthor('');
        setDate(new Date().toISOString().split('T')[0]); // Reset to today's date
        setReadTime('');
        setInstagram('');
        setAuthorPic('');
        setAuthImagrPath('');
        setAuthorDesignation('');
        setSelectedGenres([]);
        setSelectedTags([]);
        setTaggedArtists([]);
        setIsHeroBanner(false);
        setImageAlt('');
        setImageCaption('');
        setVideoUrl('');
        setPrimaryTag('Music News');
        setArticleStatus('published');
        setScheduledPublishDate('');
        setError(null);
        // Clear AI workflow state
        setAiTopic('');
        setTopicSuggestions([]);
        setSelectedResearchTopic(null);
        setResearchData(null);
        setSelectedAuthorId('');
        setArticleType('standard_news');
        setAiGenerated(false);
        setCustomPrompt('');
        setShowCustomPrompt(false);
        // Clear draft from localStorage
        localStorage.removeItem(DRAFT_KEY);
        // Close modal and show success message
        setShowClearModal(false);
        toast.success('All content cleared', { toastId: 'content-cleared' });
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            handleSaveDraft(true);
        }, 400);
        return () => clearTimeout(timer);
    }, [
        title,
        coverImgCreds,
        coverImgRightsStatus,
        coverImgRightsHolder,
        coverImgRightsEvidenceRef,
        description,
        coverImagePath,
        author,
        date,
        readTime,
        instagram,
        authImagePath,
        authorDesignation,
        selectedGenres,
        selectedTags,
        taggedArtists,
        isHeroBanner,
        imageAlt,
        imageCaption,
        videoUrl,
        primaryTag,
        articleStatus,
        scheduledPublishDate,
    ]);

    const TAG_TYPES = [
        { value: 'genre', label: 'Genre' },
        { value: 'mood', label: 'Mood' },
        { value: 'topic', label: 'Topic' },
        { value: 'era', label: 'Era' },
        { value: 'custom', label: 'Custom' },
    ];

    const fetchTags = async () => {
        try {
            const response = await allAPiServicesCall.getTags({}, getAuthConfig());
            if (response?.data?.tags) {
                setTags(response.data.tags);
            }
        } catch (error) {
            console.error('Error fetching tags:', error);
        }
    };

    const handleTagChange = (tagName) => {
        setSelectedTags(prev => {
            if (prev.includes(tagName)) {
                return prev.filter(name => name !== tagName);
            } else {
                return [...prev, tagName];
            }
        });
    };

    const handleAddNewTag = async () => {
        const trimmedName = newTagInput.trim();
        if (!trimmedName) return;

        // Check if tag already exists
        const existingTag = tags.find(t => t.name.toLowerCase() === trimmedName.toLowerCase());
        if (existingTag) {
            // Auto-select the existing tag instead of showing error
            if (!selectedTags.includes(existingTag.name)) {
                setSelectedTags(prev => [...prev, existingTag.name]);
                toast.success(`"${existingTag.name}" selected`);
            } else {
                toast.info(`"${existingTag.name}" is already selected`);
            }
            setNewTagInput('');
            setTagSearchQuery('');
            setError(null);
            return;
        }

        setAddingTag(true);
        try {
            const response = await allAPiServicesCall.createTag({
                name: trimmedName,
                type: newTagType,
                isActive: true
            }, getAuthConfig());
            if (response?.success) {
                // Refresh tags list
                await fetchTags();
                // Auto-select the new tag by name
                if (response.data?.name) {
                    setSelectedTags(prev => [...prev, response.data.name]);
                    toast.success(`"${response.data.name}" created and selected`);
                }
                setNewTagInput('');
                setTagSearchQuery('');
                setError(null);
            }
        } catch (error) {
            console.error('Error adding tag:', error);
            setError('Failed to add tag');
        } finally {
            setAddingTag(false);
        }
    };

    const handleAddNewGenre = async () => {
        const trimmedGenre = newGenreInput.trim();
        if (!trimmedGenre) return;

        // Check if genre already exists
        const existingGenre = genres.find(g => g.genreName.toLowerCase() === trimmedGenre.toLowerCase());
        if (existingGenre) {
            // Auto-select the existing genre instead of showing error
            if (!selectedGenres.includes(existingGenre.genreName)) {
                setSelectedGenres(prev => [...prev, existingGenre.genreName]);
                toast.success(`"${existingGenre.genreName}" selected`);
            } else {
                toast.info(`"${existingGenre.genreName}" is already selected`);
            }
            setNewGenreInput('');
            setGenreSearchQuery('');
            return;
        }

        setAddingGenre(true);
        try {
            const response = await allAPiServicesCall.addGenres([trimmedGenre], getAuthConfig());
            if (response?.success) {
                // Refresh genres list
                await fetchGenres();
                // Auto-select the new genre
                setSelectedGenres(prev => [...prev, trimmedGenre]);
                setNewGenreInput('');
                setGenreSearchQuery('');
                setError(null);
                toast.success(`"${trimmedGenre}" created and selected`);
            }
        } catch (error) {
            console.error('Error adding genre:', error);
        } finally {
            setAddingGenre(false);
        }
    };

    const handleArtistSearch = async (searchTerm) => {
        setArtistSearch(searchTerm);
        if (searchTerm.length < 2) {
            setArtistResults([]);
            setShowArtistDropdown(false);
            return;
        }

        try {
            const response = await allAPiServicesCall.getSearchArtist(
                { query: searchTerm },
                getAuthConfig(),
                navigate
            );
            if (response?.data) {
                setArtistResults(response.data);
                setShowArtistDropdown(true);
            }
        } catch (error) {
            console.error('Error searching artists:', error);
        }
    };

    const handleAddArtist = (artist) => {
        if (!taggedArtists.find(a => extractObjectId(a._id) === extractObjectId(artist._id))) {
            setTaggedArtists([...taggedArtists, artist]);
        }
        setArtistSearch('');
        setArtistResults([]);
        setShowArtistDropdown(false);
    };

    const handleRemoveArtist = (artistId) => {
        setTaggedArtists(taggedArtists.filter(a => extractObjectId(a._id) !== extractObjectId(artistId)));
    };

    const extractJobId = (response) => (
        response?.data?.jobId ||
        response?.jobId ||
        null
    );

    const normalizeResearchSuggestions = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.topics)) return payload.topics;
        if (Array.isArray(payload?.suggestions)) return payload.suggestions;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    };

    const normalizeArticleResult = (payload) => {
        if (!payload || typeof payload !== 'object') return null;
        if (payload?.title || payload?.description || payload?.content || payload?.body) return payload;
        if (payload?.article && typeof payload.article === 'object') return payload.article;
        if (payload?.data && typeof payload.data === 'object') return payload.data;
        return null;
    };

    const waitForNewsGenerationResult = async (jobId, timeoutMs = 240000, pollMs = 2500) => {
        const startedAt = Date.now();

        while (Date.now() - startedAt < timeoutMs) {
            const statusResponse = await allAPiServicesCall.getNewsGenerationStatus(jobId, getAuthConfig());
            if (!statusResponse) {
                await new Promise(resolve => setTimeout(resolve, pollMs));
                continue;
            }

            const state = statusResponse?.data?.state;
            if (state === 'completed') {
                return statusResponse?.data?.result;
            }

            if (state === 'failed' || statusResponse?.success === false) {
                throw new Error(statusResponse?.data?.error || statusResponse?.message || 'AI job failed');
            }

            await new Promise(resolve => setTimeout(resolve, pollMs));
        }

        throw new Error('AI generation timed out. Please try again.');
    };

    const handleSuggestTopics = async () => {
        setSuggestingTopics(true);
        setTopicSuggestions([]);
        setResearchData(null);
        setSelectedResearchTopic(null);
        try {
            const response = await allAPiServicesCall.researchNewsTopics({
                articleType,
                count: 4,
                focusArtists: taggedArtists.map(a => a.artist_name),
            }, getAuthConfig());

            let suggestions = normalizeResearchSuggestions(response?.data);
            if (suggestions.length === 0) {
                suggestions = normalizeResearchSuggestions(response);
            }

            if (suggestions.length === 0) {
                const jobId = extractJobId(response);
                if (jobId) {
                    const result = await waitForNewsGenerationResult(jobId);
                    suggestions = normalizeResearchSuggestions(result);
                }
            }

            if (suggestions.length > 0) {
                setTopicSuggestions(suggestions);
            } else {
                toast.error('No topic suggestions returned');
            }
        } catch (error) {
            console.error('Error researching topics:', error);
            toast.error('Failed to research topics');
        } finally {
            setSuggestingTopics(false);
        }
    };

    // Build a display headline from research result fields (varies by article type)
    const getResearchHeadline = (s) => {
        // standard_news: storyAngle | song_review: song + artist | album_review: album + artist
        // artist_spotlight: artist | live_coverage: event | culture_feature: topic | opinion_editorial: topicHeadline
        if (s.topicHeadline) return s.topicHeadline;
        if (s.storyAngle) return s.storyAngle;
        if (s.song && s.artist) return `${s.artist} – ${s.song}`;
        if (s.album && s.artist) return `${s.artist} – ${s.album}`;
        if (s.artist) return s.artist;
        if (s.event) return s.event;
        if (s.topic) return s.topic;
        return s.headline || s.editorialAngle || 'Topic';
    };

    const handleSelectTopic = (suggestion) => {
        const topicText = getResearchHeadline(suggestion);
        setAiTopic(topicText);
        setSelectedResearchTopic(suggestion);
        setResearchData(suggestion);
        if (suggestion.suggestedTags?.length > 0) {
            setSelectedTags(prev => {
                const newTags = suggestion.suggestedTags.filter(t => !prev.includes(t));
                return [...prev, ...newTags];
            });
        }
        if (suggestion.suggestedGenres?.length > 0) {
            setSelectedGenres(prev => {
                const newGenres = suggestion.suggestedGenres.filter(g => !prev.includes(g));
                return [...prev, ...newGenres];
            });
        }
    };

    const handleGenerateArticle = async () => {
        if (!articleType) {
            toast.error('Please select an article type');
            return;
        }
        if (!aiTopic) {
            toast.error('Please enter a topic or select one from research results');
            return;
        }
        setAiGenerating(true);
        try {
            const response = await allAPiServicesCall.writeNewsArticle({
                articleType,
                topic: aiTopic,
                researchData: researchData || undefined,
                readTime: Number(readTime) || 5,
                customPrompt: customPrompt || undefined,
                taggedArtistIds: taggedArtists.map(a => extractObjectId(a._id)),
                taggedArtistNames: taggedArtists.map(a => a.artist_name),
            }, getAuthConfig());

            let article = normalizeArticleResult(response?.data);
            if (!article) {
                article = normalizeArticleResult(response);
            }

            if (!article) {
                const jobId = extractJobId(response);
                if (jobId) {
                    const result = await waitForNewsGenerationResult(jobId);
                    article = normalizeArticleResult(result);
                }
            }

            if (article) {
                setTitle(article.title || '');
                const content = article.description || article.content || article.body || '';
                setDescription(content);
                // Auto-calculate read time from word count (~250 words/min)
                const wordCount = content.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
                const calculatedReadTime = Math.max(1, Math.round(wordCount / 250));
                const requestedReadTime = Number(readTime) || 5;
                setReadTime(String(article.readTime || calculatedReadTime));
                setPrimaryTag(article.primaryTag || 'Music News');
                if (article.tags?.length > 0) setSelectedTags(article.tags);
                if (article.genres?.length > 0) setSelectedGenres(article.genres);
                if (article.subtitle) setImageCaption(article.subtitle);
                setAiGenerated(true);

                // Show different message based on whether target was met
                if (Math.abs(calculatedReadTime - requestedReadTime) <= 1) {
                    toast.success(`Article generated! (~${wordCount} words, ${calculatedReadTime} min read)`);
                } else {
                    const targetWords = requestedReadTime * 250;
                    const percentage = Math.round((wordCount / targetWords) * 100);
                    toast.warning(`Article generated: ${wordCount} words (${percentage}% of ${targetWords} target, ${calculatedReadTime} min read)`, { autoClose: 5000 });
                }
            } else {
                toast.error('No article returned from AI');
            }
        } catch (error) {
            console.error('Error writing article:', error);
            toast.error('Failed to generate article');
        } finally {
            setAiGenerating(false);
        }
    };

        const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);

        // HTML date input returns YYYY-MM-DD format which is valid ISO 8601
        const formData = {
            title,
            description,
            coverImg: coverImagePath,
            coverImgCreds,
            coverImgRightsStatus: coverImgRightsStatus || undefined,
            coverImgRightsHolder: coverImgRightsHolder || undefined,
            coverImgRightsEvidenceRef: coverImgRightsEvidenceRef || undefined,
            author,
            publishDate: date,
            readTime,
            instagram,
            authorPic: authImagePath,
            authorDesignation,
            genre: selectedGenres[0] || undefined,
            genres: selectedGenres, // Multiple genres
            tags: selectedTags, // Selected tags
            taggedArtists: taggedArtists.map(artist => extractObjectId(artist._id)), // Artist IDs
            isHeroBanner: isHeroBanner, // Hero banner flag
            imageAlt: imageAlt, // Alt text for cover image
            imageCaption: imageCaption, // Caption for cover image
            videoUrl: videoUrl, // Video URL
            primaryTag: primaryTag, // Primary tag badge for news card
            articleType: creationMode === 'ai' ? articleType : undefined,
            authorId: selectedAuthorId || undefined,
            status: articleStatus, // 'draft' | 'published' | 'scheduled'
            scheduledPublishDate: articleStatus === 'scheduled' ? scheduledPublishDate : undefined,
        };

        try {
            await allAPiServicesCall.addNews(formData, getAuthConfig(), navigate);
            setError(null);
            localStorage.removeItem(DRAFT_KEY);
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to submit news.');
        } finally {
            setFormLoading(false);
        }
    };

    // Check image aspect ratio and show warning/cropper
    const checkImageAspectRatio = (file) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const aspectRatio = img.width / img.height;
                const isGoodAspect = aspectRatio >= 1.5 && aspectRatio <= 2.1; // Allow some flexibility around 1.82
                resolve({ width: img.width, height: img.height, aspectRatio, isGoodAspect });
            };
            img.src = URL.createObjectURL(file);
        });
    };

    const handleImageChange = async (e) => {
        const imageFile = e.target.files[0];
        if (!imageFile) return;

        if (imageFile?.size > 5 * 1024 * 1024) {
            setError('Image size should not exceed 5 MB.');
            return;
        }

        // Check aspect ratio
        const { width, height, aspectRatio, isGoodAspect } = await checkImageAspectRatio(imageFile);

        if (!isGoodAspect) {
            // Show cropper for images with wrong aspect ratio
            setAspectWarning(`Your image has aspect ratio ${aspectRatio.toFixed(2)}:1 (${width}x${height}). Recommended is ~1.82:1 (landscape). Please crop to avoid distortion.`);
            const reader = new FileReader();
            reader.onload = () => {
                setCropImageSrc(reader.result);
                setShowCropper(true);
            };
            reader.readAsDataURL(imageFile);
        } else {
            // Good aspect ratio, upload directly
            setAspectWarning(null);
            await uploadCoverImage(imageFile);
        }
    };

    const handleCropComplete = async (croppedFile) => {
        setAspectWarning(null);
        setCropImageSrc(null);
        // Reset input so same file can be selected again if needed
        if (coverImageInputRef.current) {
            coverImageInputRef.current.value = '';
        }
        await uploadCoverImage(croppedFile);
    };

    const uploadCoverImage = async (imageFile) => {
        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
            fileType: 'image/jpeg',
        };

        try {
            setImageLoading(true);
            const compressedFile = await imageCompression(imageFile, options);

            const formData = new FormData();
            formData.append('image', compressedFile);

            const response = await allAPiServicesCall.addNewsCoverPic(formData, getFormDataConfig(), navigate);
            setCoverImagePath(response.data?.path);
            setError(null);
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to upload image.');
        } finally {
            setImageLoading(false);
        }
    };


    const handleAuthImageChange = async (e) => {
        setImageLoading(true);
        let imageFile = e.target.files[0];

        if (imageFile?.size > 5 * 1024 * 1024) { // Check if file is larger than 5MB
            setError('Image size should not exceed 5 MB.');
            return;
        }

        const options = {
            maxSizeMB: 1, // Compress to 1MB
            maxWidthOrHeight: 1024, // Resize image to 1024x1024
            useWebWorker: true,
            fileType: imageFile.type,
        };

        try {
            const compressedFile = await imageCompression(imageFile, options);

            const formData = new FormData();
            formData.append('image', compressedFile);

            const response = await allAPiServicesCall.uploadAuthorImage(formData, getFormDataConfig(), navigate);
            setAuthImagrPath(response.data.path);
            setError(null);
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to submit news.');
        } finally {
            setImageLoading(false)
        }

    }

    const handleVideoChange = async (e) => {
        const videoFile = e.target.files[0];
        if (!videoFile) return;

        // Check file size (100MB limit)
        if (videoFile.size > 100 * 1024 * 1024) {
            setError('Video size should not exceed 100 MB.');
            return;
        }

        // Validate file type
        const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg'];
        if (!validTypes.includes(videoFile.type)) {
            setError('Invalid video format. Allowed formats: MP4, WebM, MOV, AVI, MPEG');
            return;
        }

        setVideoLoading(true);
        try {
            const formData = new FormData();
            formData.append('video', videoFile);

            const response = await allAPiServicesCall.uploadNewsVideo(formData, getFormDataConfig(), navigate);
            setVideoUrl(response.data?.path);
            setError(null);
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to upload video.');
        } finally {
            setVideoLoading(false);
        }
    };

    const handleRemoveVideo = () => {
        setVideoUrl('');
    };

    // Premium styles
    const sectionCardStyle = {
        background: 'var(--bg-card)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-light)',
        boxShadow: 'var(--card-shadow)',
        padding: '1.5rem',
        marginBottom: '1.5rem',
    };

    const sectionHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.25rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid var(--border-light)',
    };

    const sectionIconStyle = (bg) => ({
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.25rem',
        background: bg,
        color: 'white',
    });

    const badgeStyle = {
        background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%)',
        padding: '0.5rem 0.875rem',
        borderRadius: '20px',
        fontSize: '0.8125rem',
        fontWeight: '500',
        boxShadow: '0 2px 8px rgba(0, 49, 226, 0.25)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    };

    const tagBadgeStyle = (color) => ({
        background: `linear-gradient(135deg, ${color || '#6366f1'} 0%, ${color || '#8b5cf6'}dd 100%)`,
        padding: '0.5rem 0.875rem',
        borderRadius: '20px',
        fontSize: '0.8125rem',
        fontWeight: '500',
        boxShadow: `0 2px 8px ${color || '#6366f1'}40`,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    });

    const closeButtonStyle = {
        background: 'rgba(255,255,255,0.3)',
        border: 'none',
        borderRadius: '50%',
        width: '18px',
        height: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '0.75rem',
        color: 'white',
        padding: 0,
        lineHeight: 1,
    };

    return (
        <CommonLayout>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div className="form-page-container" style={{ maxWidth: '1000px', marginBottom: '4rem' }}>
                {/* Page Header */}
                <div style={{
                    background: 'linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '2rem 2.5rem',
                    marginBottom: '2rem',
                    color: 'white',
                    boxShadow: '0 8px 32px rgba(0, 49, 226, 0.25)',
                }}>
                    <h1 style={{ fontSize: '2rem', fontWeight: '700', margin: 0, fontFamily: "'Poppins', sans-serif" }}>
                        Create News Article
                    </h1>
                    <p style={{ margin: '0.5rem 0 0', opacity: 0.9, fontSize: '0.9375rem' }}>
                        Publish engaging news content for your audience
                    </p>
                </div>

                {/* Creation Mode Toggle */}
                <div style={sectionCardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={sectionIconStyle('linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)')}>
                            <span style={{ fontSize: '1.1rem' }}>&#x2728;</span>
                        </div>
                        <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>Choose a creation path</h3>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>Use the original manual editor below, or open the governed AI newsroom.</p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '4px' }}>
                            <button
                                type="button"
                                onClick={() => setCreationMode('manual')}
                                style={{
                                    padding: '0.5rem 1.25rem', borderRadius: '10px', border: 'none', fontWeight: '600', fontSize: '0.875rem',
                                    cursor: 'pointer', transition: 'all 0.2s ease',
                                    background: creationMode === 'manual' ? 'linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)' : 'transparent',
                                    color: creationMode === 'manual' ? 'white' : 'var(--text-secondary)',
                                    boxShadow: creationMode === 'manual' ? '0 2px 8px rgba(0, 49, 226, 0.25)' : 'none',
                                }}
                            >Manual Article</button>
                            <button
                                type="button"
                                onClick={() => navigate('/newsroom-control')}
                                style={{
                                    padding: '0.5rem 1.25rem', borderRadius: '10px', border: 'none', fontWeight: '600', fontSize: '0.875rem',
                                    cursor: 'pointer', transition: 'all 0.2s ease',
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                                    color: 'white',
                                    boxShadow: '0 2px 8px rgba(139, 92, 246, 0.35)',
                                }}
                            >Open Governed Newsroom</button>
                        </div>
                    </div>
                </div>

                {/* AI Generation Panel */}
                {creationMode === 'ai' && (
                    <div style={{
                        ...sectionCardStyle,
                        border: '2px solid rgba(139, 92, 246, 0.3)',
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.03) 0%, rgba(167, 139, 250, 0.03) 100%)',
                    }}>
                        <div style={sectionHeaderStyle}>
                            <div style={sectionIconStyle('linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)')}>
                                <span style={{ fontSize: '1.1rem' }}>&#x1F916;</span>
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>AI Article Generator</h3>
                                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>Generate article content using Gemini AI</p>
                            </div>
                        </div>

                        {/* Article Type */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Article Type <span style={{ color: 'var(--accent-pink)' }}>*</span>
                            </label>
                            <select
                                className="form-control"
                                value={articleType}
                                onChange={(e) => setArticleType(e.target.value)}
                                style={{
                                    borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem',
                                    appearance: 'auto', WebkitAppearance: 'menulist', MozAppearance: 'menulist',
                                    cursor: 'pointer',
                                }}
                            >
                                {ARTICLE_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Topic Suggestions */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                <label style={{ fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>Topic</label>
                                <button
                                    type="button"
                                    onClick={handleSuggestTopics}
                                    disabled={suggestingTopics}
                                    style={{
                                        padding: '0.375rem 1rem', borderRadius: '20px', border: '2px solid #8b5cf6',
                                        background: 'transparent', color: '#8b5cf6', fontWeight: '600', fontSize: '0.8125rem',
                                        cursor: suggestingTopics ? 'not-allowed' : 'pointer', opacity: suggestingTopics ? 0.6 : 1,
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {suggestingTopics ? 'Researching...' : 'Research Topics'}
                                </button>
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter a topic or click 'Research Topics' for AI-researched topics..."
                                value={aiTopic}
                                onChange={(e) => setAiTopic(e.target.value)}
                                style={{ borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem' }}
                            />
                        </div>

                        {/* Research Topic Cards */}
                        {topicSuggestions.length > 0 && (
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                                    Researched Topics (click to select)
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {topicSuggestions.map((suggestion, idx) => {
                                        const isSelected = selectedResearchTopic === suggestion;
                                        const headline = getResearchHeadline(suggestion);
                                        const subtitle = suggestion.editorialAngle || suggestion.primaryAngle || suggestion.coreThesis || suggestion.releaseContext || suggestion.currentSituation || suggestion.whatIsHappening || suggestion.whatHappened || '';
                                        const description = suggestion.whyItMatters || suggestion.audienceRelevance || '';
                                        return (
                                            <div
                                                key={idx}
                                                onClick={() => handleSelectTopic(suggestion)}
                                                style={{
                                                    padding: '0.875rem 1rem', borderRadius: 'var(--radius-md)',
                                                    border: isSelected ? '2px solid #8b5cf6' : '1px solid var(--border-light)',
                                                    background: isSelected ? 'rgba(139, 92, 246, 0.08)' : 'var(--bg-card)',
                                                    cursor: 'pointer', transition: 'all 0.2s ease',
                                                }}
                                            >
                                                <div style={{ fontWeight: '600', fontSize: '0.9375rem', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                                                    {headline}
                                                </div>
                                                {subtitle && (
                                                    <div style={{ fontSize: '0.8125rem', color: '#8b5cf6', fontWeight: '500', marginBottom: '0.25rem' }}>
                                                        {subtitle}
                                                    </div>
                                                )}
                                                {description && (
                                                    <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{description}</div>
                                                )}
                                                {suggestion.verifiedYoutubeUrls?.length > 0 && (
                                                    <div style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.375rem' }}>
                                                        ✓ {suggestion.verifiedYoutubeUrls.length} verified YouTube {suggestion.verifiedYoutubeUrls.length === 1 ? 'link' : 'links'}
                                                    </div>
                                                )}
                                                {(suggestion.suggestedTags?.length > 0 || suggestion.suggestedGenres?.length > 0) && (
                                                    <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                                        {suggestion.suggestedGenres?.map((genre, i) => (
                                                            <span key={`g-${i}`} style={{
                                                                padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem',
                                                                background: 'rgba(16, 185, 129, 0.1)', color: '#059669', fontWeight: '500',
                                                            }}>{genre}</span>
                                                        ))}
                                                        {suggestion.suggestedTags?.map((tag, i) => (
                                                            <span key={`t-${i}`} style={{
                                                                padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem',
                                                                background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', fontWeight: '500',
                                                            }}>{tag}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Read Time */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Target Read Time (minutes) <span style={{ color: 'var(--accent-pink)' }}>*</span>
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                value={readTime}
                                onChange={(e) => setReadTime(e.target.value)}
                                placeholder="5"
                                min="1"
                                max="30"
                                style={{ borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem' }}
                            />
                            <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                AI will generate ~250 words per minute (e.g., 8 min = ~2000 words)
                            </small>
                        </div>

                        {/* Custom Prompt (collapsible) */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <button
                                type="button"
                                onClick={() => setShowCustomPrompt(!showCustomPrompt)}
                                style={{
                                    background: 'none', border: 'none', color: '#8b5cf6', fontWeight: '600',
                                    fontSize: '0.875rem', cursor: 'pointer', padding: 0, marginBottom: showCustomPrompt ? '0.5rem' : 0,
                                }}
                            >
                                {showCustomPrompt ? '- Hide Custom Instructions' : '+ Add Custom Instructions'}
                            </button>
                            {showCustomPrompt && (
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Add custom instructions for the AI (e.g., 'Focus on the production quality', 'Write in a casual tone')..."
                                    value={customPrompt}
                                    onChange={(e) => setCustomPrompt(e.target.value)}
                                    style={{ borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem', resize: 'vertical' }}
                                />
                            )}
                        </div>

                        {/* Generate Button */}
                        <button
                            type="button"
                            onClick={handleGenerateArticle}
                            disabled={aiGenerating}
                            style={{
                                width: '100%', padding: '0.875rem', borderRadius: 'var(--radius-md)', border: 'none',
                                background: aiGenerating ? '#9ca3af' : 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                                color: 'white', fontWeight: '700', fontSize: '1rem', cursor: aiGenerating ? 'not-allowed' : 'pointer',
                                boxShadow: aiGenerating ? 'none' : '0 4px 16px rgba(139, 92, 246, 0.35)',
                                transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                            }}
                        >
                            {aiGenerating ? (
                                <>
                                    <span style={{ display: 'inline-block', width: '18px', height: '18px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                    Writing Article...
                                </>
                            ) : 'Write Article'}
                        </button>
                    </div>
                )}

                {/* AI Generated Banner */}
                {aiGenerated && (
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(167, 139, 250, 0.1) 100%)',
                        border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: 'var(--radius-md)',
                        padding: '0.875rem 1.25rem', marginBottom: '1.5rem',
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                    }}>
                        <span style={{ fontSize: '1.25rem' }}>&#x2728;</span>
                        <div>
                            <span style={{ fontWeight: '600', color: '#8b5cf6' }}>AI-generated content</span>
                            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}> &mdash; review carefully. Images must be added manually.</span>
                        </div>
                    </div>
                )}

                {/* Draft Actions */}
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button type="button" onClick={() => handleSaveDraft()} className="btn" style={{
                        background: 'var(--bg-card)', border: '2px solid var(--border-color)', borderRadius: 'var(--radius-md)',
                        padding: '0.625rem 1.25rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
                        transition: 'all 0.2s ease',
                    }}>Save Draft</button>
                    <button type="button" onClick={() => setShowClearModal(true)} className="btn" style={{
                        background: 'var(--bg-card)', border: '2px solid var(--accent-pink)', color: 'var(--accent-pink)',
                        borderRadius: 'var(--radius-md)', padding: '0.625rem 1.25rem', fontWeight: '500',
                        display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s ease',
                    }}>Clear All</button>
                    {draftStatus && (
                        <span style={{ padding: '0.625rem 1rem', background: 'var(--accent-green)', color: 'white',
                            borderRadius: 'var(--radius-md)', fontSize: '0.875rem', fontWeight: '500' }}>{draftStatus}</span>
                    )}
                </div>

                {/* Categorization Section */}
                <div style={sectionCardStyle}>
                    <div style={sectionHeaderStyle}>
                        <div style={sectionIconStyle('linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)')}>
                            <span style={{ fontSize: '1.1rem' }}>&#x1F3F7;</span>
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>Categorization</h3>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>Organize your article with genres, tags & artists</p>
                        </div>
                    </div>

                    {/* Genres */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: 'var(--text-primary)' }}>Genres</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input type="text" className="form-control" placeholder="Search or add genre..."
                                value={genreSearchQuery || newGenreInput}
                                onChange={(e) => { setGenreSearchQuery(e.target.value); setNewGenreInput(e.target.value); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewGenre(); }}}
                                disabled={addingGenre}
                                style={{ flex: 1, borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)', padding: '0.625rem 1rem' }}
                            />
                            <button type="button" onClick={handleAddNewGenre} disabled={addingGenre || !newGenreInput.trim()}
                                style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%)',
                                    border: 'none', borderRadius: 'var(--radius-md)', padding: '0 1.5rem', fontWeight: '500',
                                    color: 'white', whiteSpace: 'nowrap', cursor: 'pointer', transition: 'all 0.2s ease',
                                    opacity: (addingGenre || !newGenreInput.trim()) ? 0.6 : 1 }}>
                                {addingGenre ? <span className="spinner-border spinner-border-sm"></span> : '+ Add'}
                            </button>
                        </div>
                        <small style={{ color: '#6b7280', fontSize: '0.8125rem', marginTop: '0.5rem', display: 'block' }}>Press Enter or click Add to select/create a genre</small>

                        {/* Selected Genres Display */}
                        {selectedGenres.length > 0 && (
                            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {selectedGenres.map((genre) => (
                                    <span key={genre} style={badgeStyle}>
                                        {genre}
                                        <button type="button" onClick={() => handleMultipleGenreChange(genre)}
                                            style={closeButtonStyle} aria-label="Remove">×</button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Toggle to show/hide genre list */}
                        <button type="button" onClick={() => setShowGenreList(!showGenreList)}
                            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.875rem',
                                fontWeight: '500', padding: '0.5rem 0', cursor: 'pointer', marginTop: '0.5rem' }}>
                            {showGenreList ? '▼ Hide genre list' : '▶ Browse all genres'}
                        </button>

                        {/* Collapsible Genre List */}
                        {showGenreList && (
                            <div style={{ border: '2px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                                padding: '1rem', marginTop: '0.5rem', maxHeight: '200px', overflowY: 'auto', background: '#fafafa' }}>
                                {genres.filter(genre => !genreSearchQuery || genre.genreName.toLowerCase().includes(genreSearchQuery.toLowerCase()))
                                    .map((genre, index) => (
                                        <div key={extractObjectId(genre._id) || index} style={{ padding: '0.375rem 0' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input type="checkbox" value={genre.genreName}
                                                    checked={selectedGenres.includes(genre.genreName)}
                                                    onChange={() => handleMultipleGenreChange(genre.genreName)}
                                                    style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }}
                                                />
                                                <span style={{ color: 'var(--text-primary)' }}>{genre.genreName}</span>
                                            </label>
                                        </div>
                                    ))}
                                {genres.filter(genre => !genreSearchQuery || genre.genreName.toLowerCase().includes(genreSearchQuery.toLowerCase())).length === 0 && (
                                    <p style={{ color: '#6b7280', margin: 0 }}>No genres found. Click "+ Add" to create "{genreSearchQuery}"</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Tags */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: 'var(--text-primary)' }}>Tags</label>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <input type="text" className="form-control" placeholder="Search or add tag..."
                                value={tagSearchQuery || newTagInput}
                                onChange={(e) => { setTagSearchQuery(e.target.value); setNewTagInput(e.target.value); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewTag(); }}}
                                disabled={addingTag}
                                style={{ flex: '1 1 200px', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)', padding: '0.625rem 1rem' }}
                            />
                            <select value={newTagType} onChange={(e) => setNewTagType(e.target.value)} disabled={addingTag}
                                style={{ borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)', padding: '0.625rem 1rem',
                                    background: 'white', minWidth: '120px' }}>
                                {TAG_TYPES.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
                            </select>
                            <button type="button" onClick={handleAddNewTag} disabled={addingTag || !newTagInput.trim()}
                                style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                    border: 'none', borderRadius: 'var(--radius-md)', padding: '0 1.5rem', fontWeight: '500',
                                    color: 'white', whiteSpace: 'nowrap', cursor: 'pointer', transition: 'all 0.2s ease',
                                    opacity: (addingTag || !newTagInput.trim()) ? 0.6 : 1 }}>
                                {addingTag ? <span className="spinner-border spinner-border-sm"></span> : '+ Add Tag'}
                            </button>
                        </div>
                        <small style={{ color: '#6b7280', fontSize: '0.8125rem', marginTop: '0.5rem', display: 'block' }}>Type to search existing tags or add a new one. Select tag type for new tags.</small>

                        {/* Selected Tags Display */}
                        {selectedTags.length > 0 && (
                            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {selectedTags.map((tagName) => {
                                    const tag = tags.find(t => t.name === tagName);
                                    return (
                                        <span key={tagName} style={tagBadgeStyle(tag?.color)}>
                                            {tagName}
                                            <button type="button" onClick={() => handleTagChange(tagName)}
                                                style={closeButtonStyle} aria-label="Remove">×</button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Toggle to show/hide tag list */}
                        <button type="button" onClick={() => setShowTagList(!showTagList)}
                            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.875rem',
                                fontWeight: '500', padding: '0.5rem 0', cursor: 'pointer', marginTop: '0.5rem' }}>
                            {showTagList ? '▼ Hide tag list' : '▶ Browse all tags'}
                        </button>

                        {/* Collapsible Tag List */}
                        {showTagList && (
                            <div style={{ border: '2px solid var(--border-light)', borderRadius: 'var(--radius-md)',
                                padding: '1rem', marginTop: '0.5rem', maxHeight: '200px', overflowY: 'auto', background: '#fafafa' }}>
                                {tags.length === 0 ? (
                                    <p style={{ color: '#6b7280', margin: 0 }}>No tags available. Use the input above to add one.</p>
                                ) : (
                                    tags.filter(tag => tag.isActive)
                                        .filter(tag => !tagSearchQuery || tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase()))
                                        .map((tag) => (
                                            <div key={extractObjectId(tag._id)} style={{ padding: '0.375rem 0' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" value={tag.name}
                                                        checked={selectedTags.includes(tag.name)}
                                                        onChange={() => handleTagChange(tag.name)}
                                                        style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }}
                                                    />
                                                    <span style={{ background: tag.color || '#6366f1', color: 'white', padding: '0.125rem 0.5rem',
                                                        borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500' }}>{tag.type}</span>
                                                    <span style={{ color: 'var(--text-primary)' }}>{tag.name}</span>
                                                </label>
                                            </div>
                                        ))
                                )}
                                {tags.filter(tag => tag.isActive).filter(tag => !tagSearchQuery || tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())).length === 0 && tags.length > 0 && (
                                    <p style={{ color: '#6b7280', margin: 0 }}>No tags found. Click "+ Add Tag" to create "{tagSearchQuery}"</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Artists */}
                    <div>
                        <label style={{ fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: 'var(--text-primary)' }}>Tag Artists</label>
                        <div style={{ position: 'relative' }}>
                            <input type="text" value={artistSearch} onChange={(e) => handleArtistSearch(e.target.value)}
                                placeholder="Search for artists to tag..." className="form-control"
                                style={{ borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)', padding: '0.625rem 1rem' }}
                            />
                            {showArtistDropdown && artistResults.length > 0 && (
                                <div style={{ position: 'absolute', width: '100%', background: 'white', border: '2px solid var(--border-light)',
                                    borderRadius: 'var(--radius-md)', marginTop: '0.25rem', zIndex: 1000, maxHeight: '200px', overflowY: 'auto',
                                    boxShadow: 'var(--card-shadow)' }}>
                                    {artistResults.map((artist) => (
                                        <div key={extractObjectId(artist._id)} onClick={() => handleAddArtist(artist)}
                                            style={{ padding: '0.75rem 1rem', cursor: 'pointer', borderBottom: '1px solid var(--border-light)',
                                                transition: 'background 0.15s ease' }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}>
                                            {artist.artist_name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {taggedArtists.length > 0 && (
                            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {taggedArtists.map((artist) => (
                                    <span key={extractObjectId(artist._id)} style={{
                                        background: 'linear-gradient(135deg, var(--accent-green) 0%, #15803d 100%)',
                                        padding: '0.5rem 0.875rem', borderRadius: '20px', fontSize: '0.8125rem', fontWeight: '500',
                                        boxShadow: '0 2px 8px rgba(29, 185, 84, 0.25)', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem'
                                    }}>
                                        {artist.artist_name}
                                        <button type="button" onClick={() => handleRemoveArtist(extractObjectId(artist._id))}
                                            style={closeButtonStyle} aria-label="Remove">×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Article Content Section */}
                <div style={sectionCardStyle}>
                    <div style={sectionHeaderStyle}>
                        <div style={sectionIconStyle('linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%)')}>
                            <span style={{ fontSize: '1.1rem' }}>&#x1F4DD;</span>
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>Article Content</h3>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>Write your news article title and body</p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {/* Title */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Title <span style={{ color: 'var(--accent-pink)' }}>*</span>
                            </label>
                            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required
                                placeholder="Enter your article title..."
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.75rem 1rem', fontSize: '1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>

                        {/* Description */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Description <span style={{ color: 'var(--accent-pink)' }}>*</span>
                            </label>
                            <JoditEditor ref={editor} value={description} onChange={setDescription} />
                        </div>
                    </form>
                </div>

                {/* Author Section */}
                <div style={sectionCardStyle}>
                    <div style={sectionHeaderStyle}>
                        <div style={sectionIconStyle('linear-gradient(135deg, var(--accent-green) 0%, #15803d 100%)')}>
                            <span style={{ fontSize: '1.1rem' }}>&#x1F464;</span>
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>Author Information</h3>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>Details about the article author</p>
                        </div>
                    </div>

                    {/* Author Persona Selector */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                            Select Author Persona
                        </label>
                        <select
                            className="form-control"
                            value={selectedAuthorId}
                            onChange={(e) => handleAuthorSelect(e.target.value)}
                            disabled={authorsLoading}
                            style={{
                                borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem',
                                appearance: 'auto', WebkitAppearance: 'menulist', MozAppearance: 'menulist',
                                cursor: 'pointer', border: '2px solid var(--border-light)',
                            }}
                        >
                            <option value="">-- Manual Entry --</option>
                            {authors.map(a => (
                                <option key={extractObjectId(a._id) || a.slug} value={extractObjectId(a._id) || a._id}>
                                    {a.name} — {a.designation}
                                </option>
                            ))}
                        </select>
                        <small style={{ color: '#6b7280', fontSize: '0.8125rem', marginTop: '0.375rem', display: 'block' }}>
                            Select a persona to auto-fill author name, designation & photo. Or choose "Manual Entry" to type your own.
                        </small>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                        {/* Author Name */}
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Author Name <span style={{ color: 'var(--accent-pink)' }}>*</span>
                            </label>
                            <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)} required
                                placeholder="John Doe"
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>

                        {/* Author Designation */}
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Author Designation
                            </label>
                            <input type="text" value={authorDesignation} onChange={(e) => setAuthorDesignation(e.target.value)}
                                placeholder="Senior Music Journalist"
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>

                        {/* Instagram */}
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Instagram Handle
                            </label>
                            <input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)}
                                placeholder="@username"
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>

                        {/* Date */}
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Publish Date <span style={{ color: 'var(--accent-pink)' }}>*</span>
                            </label>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>
                    </div>

                    {/* Author Picture */}
                    <div style={{ marginTop: '1.25rem' }}>
                        <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                            Author Picture
                        </label>
                        <input type="file" onChange={handleAuthImageChange} accept="image/*"
                            style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                padding: '0.625rem 1rem', background: 'white' }}
                        />
                        {authImagePath && (
                            <div style={{ marginTop: '1rem' }}>
                                <img src={authImagePath} alt="Author Preview"
                                    style={{ height: '120px', width: '120px', objectFit: 'cover', borderRadius: '50%',
                                        border: '3px solid var(--primary)', boxShadow: '0 4px 12px rgba(0,49,226,0.2)' }}
                                />
                            </div>
                        )}
                        {imageLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                                <span style={{ color: '#6b7280' }}>Uploading...</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Media Section */}
                <div style={sectionCardStyle}>
                    <div style={sectionHeaderStyle}>
                        <div style={sectionIconStyle('linear-gradient(135deg, var(--accent-pink) 0%, #dc2626 100%)')}>
                            <span style={{ fontSize: '1.1rem' }}>&#x1F5BC;</span>
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>Media</h3>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>Cover image and video content</p>
                        </div>
                    </div>

                    {/* Cover Image */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                            Cover Image
                        </label>
                        <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)', padding: '0.75rem 1rem',
                            borderRadius: 'var(--radius-md)', marginBottom: '0.75rem', border: '1px solid #bfdbfe' }}>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#1e40af' }}>
                                <strong>Image Guidelines:</strong> Use landscape images with aspect ratio ~1.82:1 (e.g., 1024x563 pixels). Square or portrait images will be cropped.
                            </p>
                        </div>
                        <input type="file" ref={coverImageInputRef} onChange={handleImageChange} accept="image/*"
                            style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                padding: '0.625rem 1rem', background: 'white' }}
                        />
                        {aspectWarning && (
                            <div style={{ background: '#fef3c7', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)',
                                marginTop: '0.75rem', border: '1px solid #fcd34d', color: '#92400e', fontSize: '0.8125rem' }}>
                                {aspectWarning}
                            </div>
                        )}
                        {coverImagePath && (
                            <div style={{ marginTop: '1rem' }}>
                                <p style={{ color: '#6b7280', marginBottom: '0.5rem', fontSize: '0.8125rem' }}>Preview:</p>
                                <div style={{ width: '100%', maxWidth: '400px', aspectRatio: '1.82', overflow: 'hidden',
                                    borderRadius: 'var(--radius-md)', border: '3px solid var(--border-light)', boxShadow: 'var(--card-shadow)' }}>
                                    <img src={coverImagePath} alt="Cover Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                            </div>
                        )}
                        {imageLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                                <span style={{ color: '#6b7280' }}>Uploading image...</span>
                            </div>
                        )}
                    </div>

                    {/* Image Metadata */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Cover Image Credits
                            </label>
                            <input type="text" value={coverImgCreds} onChange={(e) => setCoverImgCreds(e.target.value)}
                                placeholder="Photo by John Doe / Getty Images"
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Alt Text <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>(for accessibility)</span>
                            </label>
                            <input type="text" value={imageAlt} onChange={(e) => setImageAlt(e.target.value)}
                                placeholder="Describe the image for screen readers..."
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Image Rights Status <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>(optional for manual publishing)</span>
                            </label>
                            <select value={coverImgRightsStatus} onChange={(e) => setCoverImgRightsStatus(e.target.value)}
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            >
                                <option value="">-- Select rights status --</option>
                                <option value="verified">Verified</option>
                                <option value="pending">Pending</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Image Rights Holder <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>(optional for manual publishing)</span>
                            </label>
                            <input type="text" value={coverImgRightsHolder} onChange={(e) => setCoverImgRightsHolder(e.target.value)}
                                maxLength={200}
                                placeholder="e.g. Sara Komatsu / WikiPortraits, CC BY-SA 4.0"
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Rights Evidence URL <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>(optional for manual publishing)</span>
                            </label>
                            <input type="text" value={coverImgRightsEvidenceRef} onChange={(e) => setCoverImgRightsEvidenceRef(e.target.value)}
                                maxLength={500}
                                placeholder="Link to the license source (e.g. Wikimedia Commons file page)"
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Image Caption <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>(optional)</span>
                            </label>
                            <input type="text" value={imageCaption} onChange={(e) => setImageCaption(e.target.value)}
                                placeholder="Caption to display below the image..."
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                        </div>
                    </div>

                    {/* Video Upload */}
                    <div>
                        <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                            Video <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>(optional - MP4, WebM, MOV, max 100MB)</span>
                        </label>
                        <input type="file" onChange={handleVideoChange}
                            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
                            disabled={videoLoading}
                            style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                padding: '0.625rem 1rem', background: 'white' }}
                        />
                        {videoLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                                <span style={{ color: '#6b7280' }}>Uploading video...</span>
                            </div>
                        )}
                        {videoUrl && (
                            <div style={{ marginTop: '1rem' }}>
                                <p style={{ color: 'var(--accent-green)', marginBottom: '0.5rem', fontWeight: '500' }}>Video uploaded successfully</p>
                                <video src={videoUrl} controls
                                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: 'var(--radius-md)',
                                        border: '2px solid var(--border-light)' }}
                                />
                                <div style={{ marginTop: '0.75rem' }}>
                                    <button type="button" onClick={handleRemoveVideo}
                                        style={{ background: 'white', border: '2px solid var(--accent-pink)', color: 'var(--accent-pink)',
                                            borderRadius: 'var(--radius-md)', padding: '0.5rem 1rem', fontWeight: '500', cursor: 'pointer',
                                            transition: 'all 0.2s ease' }}>
                                        Remove Video
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Settings Section */}
                <div style={sectionCardStyle}>
                    <div style={sectionHeaderStyle}>
                        <div style={sectionIconStyle('linear-gradient(135deg, #f59e0b 0%, #d97706 100%)')}>
                            <span style={{ fontSize: '1.1rem' }}>&#x2699;</span>
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>Settings</h3>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>Additional article options</p>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                        {/* Article Status */}
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Article Status <span style={{ color: 'var(--accent-pink)' }}>*</span>
                            </label>
                            <select
                                className="form-control"
                                value={articleStatus}
                                onChange={(e) => setArticleStatus(e.target.value)}
                                style={{
                                    borderRadius: 'var(--radius-md)', padding: '0.75rem 1rem',
                                    appearance: 'auto', WebkitAppearance: 'menulist', MozAppearance: 'menulist',
                                    cursor: 'pointer', border: '2px solid var(--border-light)',
                                }}
                            >
                                <option value="published">Published (Live Now)</option>
                                <option value="draft">Draft (Not Visible)</option>
                                <option value="scheduled">Scheduled (Auto-Publish)</option>
                            </select>
                            <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                {articleStatus === 'draft' && 'Article will be saved but not visible on the site'}
                                {articleStatus === 'published' && 'Article will be immediately visible on the site'}
                                {articleStatus === 'scheduled' && 'Article will auto-publish at the scheduled date/time'}
                            </small>
                        </div>

                        {/* Scheduled Publish Date (only show if status is 'scheduled') */}
                        {articleStatus === 'scheduled' && (
                            <div>
                                <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                    Scheduled Publish Date & Time <span style={{ color: 'var(--accent-pink)' }}>*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    value={scheduledPublishDate}
                                    onChange={(e) => setScheduledPublishDate(e.target.value)}
                                    required={articleStatus === 'scheduled'}
                                    style={{
                                        width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                        padding: '0.625rem 1rem', transition: 'border-color 0.2s ease'
                                    }}
                                />
                                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                    Timezone: Eastern Time (ET) - Virginia
                                </small>
                            </div>
                        )}

                        {/* Primary Tag */}
                        <div>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', color: 'var(--text-primary)' }}>
                                Primary Tag <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>(displayed on news card)</span>
                            </label>
                            <input type="text" value={primaryTag} onChange={(e) => setPrimaryTag(e.target.value)}
                                placeholder="Music News"
                                style={{ width: '100%', borderRadius: 'var(--radius-md)', border: '2px solid var(--border-light)',
                                    padding: '0.625rem 1rem', transition: 'border-color 0.2s ease' }}
                            />
                            <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                This tag will be displayed as a badge on the news card image
                            </small>
                        </div>

                        {/* Hero Banner Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ background: isHeroBanner ? 'linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%)' : '#e5e7eb',
                                padding: '1.25rem', borderRadius: 'var(--radius-md)', width: '100%',
                                border: isHeroBanner ? '2px solid var(--primary)' : '2px solid var(--border-light)', transition: 'all 0.3s ease' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={isHeroBanner} onChange={(e) => setIsHeroBanner(e.target.checked)}
                                        style={{ width: '20px', height: '20px', accentColor: 'white' }}
                                    />
                                    <div>
                                        <span style={{ fontWeight: '600', color: isHeroBanner ? 'white' : 'var(--text-primary)', display: 'block' }}>
                                            Set as Homepage Main Article
                                        </span>
                                        <small style={{ color: isHeroBanner ? 'rgba(255,255,255,0.85)' : '#6b7280' }}>
                                            Feature this article on the homepage. Use Page Management later if you need to clear or fine-tune the homepage banner.
                                        </small>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{ background: '#fef2f2', border: '2px solid #fecaca', borderRadius: 'var(--radius-md)',
                        padding: '1rem', marginBottom: '1.5rem', color: '#dc2626', fontWeight: '500' }}>
                        {error}
                    </div>
                )}

                {/* Submit Actions */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <button type="submit" disabled={imageLoading || formLoading} onClick={handleSubmit}
                        style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%)',
                            border: 'none', borderRadius: 'var(--radius-md)', padding: '0.875rem 2.5rem', fontWeight: '600',
                            color: 'white', fontSize: '1rem', cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,49,226,0.3)',
                            transition: 'all 0.2s ease', opacity: (imageLoading || formLoading) ? 0.6 : 1 }}>
                        {formLoading ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span className="spinner-border spinner-border-sm"></span>
                                {articleStatus === 'draft' ? 'Saving Draft...' : articleStatus === 'scheduled' ? 'Scheduling...' : 'Publishing...'}
                            </span>
                        ) : articleStatus === 'draft' ? 'Save as Draft' : articleStatus === 'scheduled' ? 'Schedule Article' : 'Publish Article'}
                    </button>
                    <button type="button" onClick={() => setShowClearModal(true)} disabled={imageLoading || videoLoading}
                        style={{ background: 'white', border: '2px solid var(--accent-pink)', color: 'var(--accent-pink)',
                            borderRadius: 'var(--radius-md)', padding: '0.875rem 2rem', fontWeight: '600', fontSize: '1rem',
                            cursor: 'pointer', transition: 'all 0.2s ease' }}>
                        Clear All
                    </button>
                </div>
            </div>

            {/* Image Cropper Modal */}
            <ImageCropper
                show={showCropper}
                onHide={() => {
                    setShowCropper(false);
                    setCropImageSrc(null);
                    if (coverImageInputRef.current) {
                        coverImageInputRef.current.value = '';
                    }
                }}
                imageSrc={cropImageSrc}
                onCropComplete={handleCropComplete}
                aspectRatio={TARGET_ASPECT_RATIO}
                title="Crop Cover Image"
            />

            {/* Clear All Confirmation Modal */}
            <ConfirmationModal
                show={showClearModal}
                handleClose={() => setShowClearModal(false)}
                onConfirm={handleClearAll}
                title="Clear All Content"
                message="Are you sure you want to clear all content? This action cannot be undone."
                confirmText="Clear All"
                cancelText="Cancel"
                variant="danger"
            />
        </CommonLayout>
    );
};

export default AddNews;
